import { useCallback } from 'react';
import moment from 'moment/moment';
import slugify from 'slugify';
import { MeasurementDateName } from 'api';
import { CapTableVersion } from 'common/types/company';
import { MOMENT_DEFAULT_DATE_FORMAT, toString } from 'utilities';
import {
  WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION,
  WATERFALL_QUERY_PARAM_EXIT_DATE,
  WATERFALL_QUERY_PARAM_EXIT_ENTERPRISE_VALUE,
  WATERFALL_QUERY_PARAM_EXIT_EQUITY_VALUE
} from '../constants';

interface UseWaterfallUrlSyncParams {
  isFirmPage: boolean;
  updateUrlParams: (params: Record<string, string | undefined>) => void;
  companyList?: Array<{ company_id: number; company_name?: string }> | null;
  capTableList?: CapTableVersion[] | null;
  companyMeasurementDates?: MeasurementDateName[];
}

interface WaterfallState {
  selectedCompanyId?: number;
  selectedCaptable?: number;
  companySelectedMeasurementDate?: { id: number; date: string };
  exitDate?: string;
  equityValue?: number;
  enterpriseValue?: number;
}

export const useWaterfallUrlSync = ({
  isFirmPage,
  updateUrlParams,
  companyList,
  capTableList,
  companyMeasurementDates,
}: UseWaterfallUrlSyncParams) => {
  // Helper function to create slugified parameters
  const createSlugifiedParam = useCallback((name: string, id: number) => {
    const slugifiedName = slugify(name, { lower: true });
    return `${slugifiedName}-${id}`;
  }, []);

  // Update company parameter in URL
  const updateCompanyUrl = useCallback(
    (companyId: number) => {
      if (isFirmPage) {
        const company = companyList?.find(c => c.company_id === companyId);
        if (company) {
          const companySlug = createSlugifiedParam(company.company_name || 'company', company.company_id);
          updateUrlParams({ company: companySlug });
        } else {
          updateUrlParams({ company: toString(companyId) });
        }
      }
      // For company pages, do not set the company parameter in URL
      // since the company is already determined by the page route
    },
    [isFirmPage, companyList, createSlugifiedParam, updateUrlParams]
  );

  // Update cap table parameter in URL
  const updateCapTableUrl = useCallback(
    (capTableId: number) => {
      const capTable = capTableList?.find(ct => ct.id === capTableId);
      if (capTable?.id) {
        const capTableSlug = createSlugifiedParam(capTable.name || 'captable', capTable.id);
        updateUrlParams({ [WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION]: capTableSlug });
      } else {
        updateUrlParams({ [WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION]: toString(capTableId) });
      }
    },
    [capTableList, createSlugifiedParam, updateUrlParams]
  );

  // Update measurement date parameter in URL
  const updateMeasurementDateUrl = useCallback(
    (measurementDate: { id?: number; date: string }) => {
      if (isFirmPage && measurementDate.id) {
        const dateSlug = moment(measurementDate.date).format('YYYY-MM-DD');
        const dateParam = `${dateSlug}-${measurementDate.id}`;
        updateUrlParams({ date: dateParam });
      } else if (!isFirmPage && measurementDate.id) {
        // For company pages, persist the measurement date ID
        updateUrlParams({ date: toString(measurementDate.id) });
      } else {
        updateUrlParams({ date: undefined });
      }
    },
    [isFirmPage, updateUrlParams]
  );

  // Update exit date parameter in URL
  const updateExitDateUrl = useCallback(
    (exitDate: string) => {
      const formattedDate = moment(exitDate).format('YYYY-MM-DD');
      updateUrlParams({ [WATERFALL_QUERY_PARAM_EXIT_DATE]: formattedDate });
    },
    [updateUrlParams]
  );

  // Update equity value parameter in URL
  const updateEquityValueUrl = useCallback(
    (equityValue: number) => {
      updateUrlParams({
        [WATERFALL_QUERY_PARAM_EXIT_EQUITY_VALUE]: toString(equityValue),
        [WATERFALL_QUERY_PARAM_EXIT_ENTERPRISE_VALUE]: undefined
      });
    },
    [updateUrlParams]
  );

  const updateEnterpriseValueUrl = useCallback(
    (enterpriseValue: number) => {
      // Assuming there's a separate query param for enterprise value if needed
      updateUrlParams({
        [WATERFALL_QUERY_PARAM_EXIT_ENTERPRISE_VALUE]: toString(enterpriseValue),
        [WATERFALL_QUERY_PARAM_EXIT_EQUITY_VALUE]: undefined,
      });
    },
    [updateUrlParams]
  );

  // Parse URL parameters back to state values
  const parseUrlParams = useCallback(
    (urlParams: Record<string, string | undefined>): Partial<WaterfallState> => {
      const parsedState: Partial<WaterfallState> = {};

      // Parse company ID from URL
      if (urlParams.company) {
        const companyIdFromUrl = urlParams.company.split('-').pop();
        if (companyIdFromUrl && !isNaN(Number(companyIdFromUrl))) {
          parsedState.selectedCompanyId = Number(companyIdFromUrl);
        }
      }

      // Parse measurement date from URL
      if (urlParams.date) {
        let measurementDateIdFromUrl: string;
        
        if (isFirmPage) {
          // For firm pages, format is "YYYY-MM-DD-id"
          measurementDateIdFromUrl = urlParams.date.split('-').pop() || '';
        } else {
          // For company pages, format is just the ID
          measurementDateIdFromUrl = urlParams.date;
        }
        
        if (measurementDateIdFromUrl) {
          const measurementDateFromUrl = companyMeasurementDates?.find(
            md => md.id && toString(md.id) === toString(measurementDateIdFromUrl)
          );
          if (measurementDateFromUrl?.id) {
            parsedState.companySelectedMeasurementDate = measurementDateFromUrl as { id: number; date: string };
          }
        }
      }

      // Parse cap table from URL
      if (urlParams[WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION]) {
        // For both firm and company pages, format is "name-id"
        const capTableIdFromUrl = urlParams[WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION].split('-').pop() || '';
        
        if (capTableIdFromUrl && !Number.isNaN(Number(capTableIdFromUrl))) {
          parsedState.selectedCaptable = Number(capTableIdFromUrl);
        }
      }

      // Parse exit date from URL
      if (urlParams[WATERFALL_QUERY_PARAM_EXIT_DATE]) {
        const urlDate = moment(urlParams[WATERFALL_QUERY_PARAM_EXIT_DATE], 'YYYY-MM-DD');
        if (urlDate.isValid()) {
          parsedState.exitDate = urlDate.format(MOMENT_DEFAULT_DATE_FORMAT);
        }
      }

      // Parse equity value from URL
      if (urlParams[WATERFALL_QUERY_PARAM_EXIT_EQUITY_VALUE]) {
        const equityValueFromUrl = Number(urlParams[WATERFALL_QUERY_PARAM_EXIT_EQUITY_VALUE]);
        if (!Number.isNaN(equityValueFromUrl)) {
          parsedState.equityValue = equityValueFromUrl;
        }
      }

      if (urlParams[WATERFALL_QUERY_PARAM_EXIT_ENTERPRISE_VALUE]) {
        const enterpriseValueFromUrl = Number(urlParams[WATERFALL_QUERY_PARAM_EXIT_ENTERPRISE_VALUE]);
        if (!Number.isNaN(enterpriseValueFromUrl)) {
          parsedState.enterpriseValue = enterpriseValueFromUrl;
        }
      }

      return parsedState;
    },
    [companyMeasurementDates, isFirmPage]
  );

  return {
    updateCompanyUrl,
    updateCapTableUrl,
    updateMeasurementDateUrl,
    updateExitDateUrl,
    updateEquityValueUrl,
    updateEnterpriseValueUrl,
    parseUrlParams,
  };
};
